import { format } from 'date-fns';
import type { Locale } from 'date-fns';
import { enUS, es, fr } from 'date-fns/locale';

import {
  PublishTradingOfferDraft,
  PublisherPartner,
} from 'modules/trading/modals/PublishTradingOffer/types/PublishTradingOfferModalContextType';
import { formatCurrencyDecimals, formatNumber } from 'utils/formats';
import { TradingOfferType } from 'types/TradingOffer';

import { getPublisherAddressLine } from './getPublisherAddressLine';

const DATE_LOCALES: Record<string, Locale> = {
  es,
  en: enUS,
  fr,
};

const PENDING = '-';

const replaceTag = (html: string, tagTitle: string, value: string): string => {
  const regex = new RegExp(`\\{+${tagTitle}\\}+`, 'g');
  return html.replace(regex, value);
};

export type ApplyEquityMarketPublishContractPreviewTagsArgs = {
  html: string;
  offerType: TradingOfferType;
  publisherPartner: PublisherPartner | null;
  tradingOffer: PublishTradingOfferDraft | null;
  language: string;
  operationReferencePlaceholder: string;
};

export const applyEquityMarketPublishContractPreviewTags = ({
  html,
  offerType,
  publisherPartner,
  tradingOffer,
  language,
  operationReferencePlaceholder,
}: ApplyEquityMarketPublishContractPreviewTagsArgs): string => {
  const langKey = language?.split('-')[0] || 'es';
  const dateLocale = DATE_LOCALES[langKey] || es;

  const sharesTotal = tradingOffer?.sharesCount?.total ?? 0;
  const sharePrice = tradingOffer?.sharePrice ?? 0;
  const totalPrice = sharesTotal * sharePrice;
  const shareClassLabel = tradingOffer?.className || '-';

  const publisherName = publisherPartner?.name?.trim() || PENDING;
  const publisherCif = publisherPartner?.cif?.trim() || PENDING;
  const publisherAddress =
    getPublisherAddressLine(publisherPartner) || PENDING;
  const publisherIban =
    publisherPartner?.bankAccountNumber?.trim() || PENDING;

  const isSell = offerType === TradingOfferType.SELL;

  const sellerName = isSell ? publisherName : PENDING;
  const sellerCif = isSell ? publisherCif : PENDING;
  const sellerAddress = isSell ? publisherAddress : PENDING;
  const sellerIban = isSell ? publisherIban : PENDING;
  const sellerAccountHolder = isSell ? publisherName : PENDING;

  const buyerName = isSell ? PENDING : publisherName;
  const buyerCif = isSell ? PENDING : publisherCif;
  const buyerAddress = isSell ? PENDING : publisherAddress;

  let result = html;

  result = replaceTag(result, 'MERCADO_NOMBRE_VENDEDOR', sellerName);
  result = replaceTag(result, 'MERCADO_DNI_VENDEDOR', sellerCif);
  result = replaceTag(result, 'MERCADO_DOMICILIO_VENDEDOR', sellerAddress);
  result = replaceTag(result, 'MERCADO_IBAN_VENDEDOR', sellerIban);
  result = replaceTag(
    result,
    'MERCADO_TITULAR_CUENTA_VENDEDOR',
    sellerAccountHolder
  );

  result = replaceTag(result, 'MERCADO_NOMBRE_COMPRADOR', buyerName);
  result = replaceTag(result, 'MERCADO_DNI_COMPRADOR', buyerCif);
  result = replaceTag(result, 'MERCADO_DOMICILIO_COMPRADOR', buyerAddress);

  result = replaceTag(
    result,
    'MERCADO_NUM_ACCIONES',
    formatNumber(sharesTotal) || PENDING
  );
  result = replaceTag(result, 'MERCADO_CLASE_ACCIONES', shareClassLabel);
  result = replaceTag(
    result,
    'MERCADO_PRECIO_ACCION',
    formatCurrencyDecimals(sharePrice) || PENDING
  );
  result = replaceTag(
    result,
    'MERCADO_PRECIO_TOTAL',
    formatCurrencyDecimals(totalPrice) || PENDING
  );

  result = replaceTag(
    result,
    'MERCADO_FECHA',
    format(new Date(), 'PPP', { locale: dateLocale })
  );
  result = replaceTag(result, 'MERCADO_HORA', format(new Date(), 'HH:mm'));
  result = replaceTag(
    result,
    'MERCADO_REFERENCIA_OPERACION',
    operationReferencePlaceholder
  );

  return result;
};
